import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BoardGrid } from '../components/BoardGrid';
import { usePublishedBoard } from '../hooks/usePublishedBoard';
import type { Room, ScheduleItem } from '../types';

/** Parse room filter from ?room= or ?rooms= (comma-separated ids or names). */
function parseRoomFilter(searchParams: URLSearchParams): string[] {
  const raw = searchParams.get('room') ?? searchParams.get('rooms') ?? '';
  if (!raw.trim()) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

function filterRoomsByQuery(rooms: Room[], params: string[]): Room[] {
  if (params.length === 0) return rooms;
  const lowerParams = params.map((p) => p.toLowerCase());
  return rooms.filter(
    (r) =>
      params.includes(r.id) ||
      lowerParams.includes(r.name.toLowerCase())
  );
}

export function KioskBoardPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const { publishedVersion, isLoading, error } = usePublishedBoard(eventId ?? null);

  const roomFilterParams = useMemo(
    () => parseRoomFilter(searchParams),
    [searchParams]
  );

  const boardData = useMemo(() => {
    if (!publishedVersion || !eventId) return null;

    const { rooms, timeslots, sessions, schedule } = publishedVersion.snapshot;
    const orderedTimeslots = [...timeslots].sort(
      (a, b) => (a.indexInDay ?? 0) - (b.indexInDay ?? 0)
    );
    const filteredRooms = filterRoomsByQuery(rooms, roomFilterParams);
    const scheduleItems: ScheduleItem[] = schedule.map((s, i) => ({
      id: `pub-${i}`,
      eventId,
      ...s,
    }));

    return {
      rooms: filteredRooms,
      timeslots: orderedTimeslots,
      sessions,
      scheduleItems,
    };
  }, [publishedVersion, eventId, roomFilterParams]);

  if (isLoading) {
    return (
      <div className="kiosk-board-page flex items-center justify-center">
        <div className="inline-block w-10 h-10 border-4 border-primary-200 border-t-primary-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="kiosk-board-page flex items-center justify-center p-8">
        <p className="text-gray-600 text-center">{error}</p>
      </div>
    );
  }

  if (!publishedVersion || !boardData) {
    return (
      <div className="kiosk-board-page flex items-center justify-center p-8">
        <p className="text-gray-500 text-center">No schedule has been published yet.</p>
      </div>
    );
  }

  if (boardData.rooms.length === 0) {
    return (
      <div className="kiosk-board-page flex items-center justify-center p-8">
        <p className="text-gray-500 text-center">
          {roomFilterParams.length > 0
            ? 'No matching room for this filter.'
            : 'No rooms configured.'}
        </p>
      </div>
    );
  }

  if (boardData.timeslots.length === 0) {
    return (
      <div className="kiosk-board-page flex items-center justify-center p-8">
        <p className="text-gray-500 text-center">No timeslots configured.</p>
      </div>
    );
  }

  return (
    <div className="kiosk-board-page">
      <BoardGrid
        rooms={boardData.rooms}
        timeslots={boardData.timeslots}
        sessions={boardData.sessions}
        scheduleItems={boardData.scheduleItems}
        scrollToCurrentTime
        scrollContainerClassName="board-scroll-container--kiosk"
        variant="kiosk"
      />
    </div>
  );
}
